import React from "react";

const Pulse = ({ className = "", style }) => (
  <div
    className={`animate-pulse rounded-md bg-gray-200/80 ${className}`}
    style={style}
  />
);

export const AiSettingsMasterCardSkeleton = () => (
  <div className="lg:col-span-2 rounded-xl border border-blue-200 bg-gradient p-6 text-white shadow-md">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
      <div className="min-w-0 flex-1 space-y-3">
        <Pulse className="h-4 w-40 bg-white/25" />
        <Pulse className="h-8 w-72 max-w-full bg-white/30 rounded-lg" />
        <div className="space-y-2 max-w-xl pt-1">
          <Pulse className="h-3 w-full bg-white/20" />
          <Pulse className="h-3 w-[95%] bg-white/20" />
          <Pulse className="h-3 w-[88%] bg-white/20" />
        </div>
        <Pulse className="h-7 w-28 rounded-full bg-white/20" />
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <Pulse className="h-7 w-12 rounded-full bg-white/30" />
        <Pulse className="h-3 w-20 bg-white/20" />
      </div>
    </div>
  </div>
);

export const AiSettingsGlanceSkeleton = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col gap-4">
    <Pulse className="h-3 w-24" />
    <div className="grid grid-cols-2 gap-3 flex-1">
      <div className="rounded-lg bg-[#ECF0FD] px-3 py-3 border border-blue-100 space-y-2">
        <Pulse className="h-3 w-20" />
        <Pulse className="h-8 w-14" />
      </div>
      <div className="rounded-lg bg-gray-50 px-3 py-3 border border-gray-100 space-y-2">
        <Pulse className="h-3 w-24" />
        <Pulse className="h-8 w-10" />
      </div>
    </div>
    <Pulse className="h-4 w-40" />
  </div>
);

export const AiSettingsHelpCardSkeleton = () => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="flex items-center justify-between gap-3 px-5 py-4">
      <div className="flex items-center gap-3 min-w-0">
        <Pulse className="h-9 w-9 shrink-0 rounded-lg" />
        <div className="min-w-0 space-y-2 flex-1">
          <Pulse className="h-4 w-44" />
          <Pulse className="h-3 w-64 max-w-full" />
        </div>
      </div>
      <Pulse className="h-5 w-5 shrink-0 rounded" />
    </div>
  </div>
);

const AiSettingsChildRowSkeleton = () => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex items-center gap-2">
        <Pulse className="h-4 w-36" />
        <Pulse className="h-4 w-4 rounded-full" />
      </div>
      <Pulse className="h-3 w-full max-w-md" />
      <Pulse className="h-3 w-[88%] max-w-sm" />
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <Pulse className="h-6 w-12 rounded-full" />
      <Pulse className="h-7 w-12 rounded-full" />
    </div>
  </div>
);

const AiSettingsModuleRowSkeleton = ({ expanded = false }) => (
  <div className="px-4 sm:px-5 py-4 flex items-start gap-4">
    <Pulse className="h-11 w-11 shrink-0 rounded-xl" />
    <div className="min-w-0 flex-1">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 space-y-2 flex-1">
          <Pulse className="h-5 w-40" />
          <Pulse className="h-3 w-full max-w-lg" />
          <Pulse className="h-3 w-[90%] max-w-md" />
          {expanded ? <Pulse className="h-3 w-36" /> : null}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Pulse className="h-6 w-14 rounded-full" />
          <Pulse className="h-7 w-12 rounded-full" />
        </div>
      </div>
      {expanded ? (
        <div className="mt-4 space-y-2">
          <AiSettingsChildRowSkeleton />
          <AiSettingsChildRowSkeleton />
          <AiSettingsChildRowSkeleton />
        </div>
      ) : null}
    </div>
  </div>
);

export const AiSettingsModulesSkeleton = () => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-2">
        <Pulse className="h-5 w-40" />
        <Pulse className="h-3 w-80 max-w-full" />
      </div>
      <div className="flex items-center gap-2">
        <Pulse className="h-3 w-28" />
        <Pulse className="h-3 w-20" />
      </div>
    </div>

    <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/60">
      <Pulse className="h-[38px] w-full max-w-md rounded-lg" />
    </div>

    <div className="divide-y divide-gray-100">
      <AiSettingsModuleRowSkeleton expanded />
      <AiSettingsModuleRowSkeleton />
      <AiSettingsModuleRowSkeleton />
      <AiSettingsModuleRowSkeleton />
    </div>
  </div>
);

export const AiSettingsPageSkeleton = () => (
  <div className="space-y-6">
    <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
      <AiSettingsMasterCardSkeleton />
      <AiSettingsGlanceSkeleton />
    </div>

    <AiSettingsHelpCardSkeleton />
    <AiSettingsModulesSkeleton />
  </div>
);

export default AiSettingsPageSkeleton;
